import { Dot, Field } from '@/components';
import { useConfirm } from '@/hooks';
import { trans } from '@/i18n';
import { ResponsibilityService } from '@/pages/services/responsibility';
import { ISelect } from '@/types';
import { msgMaxLength, showError } from '@/utils';
import { Button, Dialog, Flex, GridItem, Input, Portal, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { BiTrashAlt } from 'react-icons/bi';
import { FaPlus } from 'react-icons/fa';
import Select, { components } from 'react-select';

interface IProps {
  name: string;
  label: string;
  placeholder?: string;
  control?: Control<any>;
  errors: FieldErrors<any>;
  width?: string | number;
  onChange?: (val: ISelect | null) => void;
  getValues?: (name: string) => any;
  disabled?: boolean;
  // New props for customization
  options?: ISelect[];
  disableSearch?: boolean;
  disableDot?: boolean;
  disableDelete?: boolean;
  disableCreate?: boolean;
  isRequired?: boolean;
  msgNoOption?: string;
  placeholderFontSize?: number;
}

const BaseSelectResponsibility = ({
  name,
  label,
  placeholder,
  control,
  errors,
  width,
  onChange,
  disabled,
  getValues,
  // New props
  options: externalOptions,
  disableSearch = false,
  disableDot = false,
  disableDelete = false,
  disableCreate = false,
  isRequired = false,
  msgNoOption = trans('responsibility.noOption'),
  placeholderFontSize,
}: IProps) => {
  const gridProps: any = { colSpan: 'auto' };
  if (width) gridProps.width = width;
  const [options, setOptions] = useState<ISelect[]>([]);
  const [newResponsibility, setNewResponsibility] = useState('');

  const [showModal, setShowModal] = useState(false);

  const sv = new ResponsibilityService();
  const { toggleConfirm } = useConfirm();

  const fetchResponsibilities = async () => {
    try {
      const res = await sv.findAll({ orderBy: 'desc', orderKey: 'name' });
      const options: ISelect[] =
        res?.map((x: any) => ({
          label: x.name,
          value: String(x.id),
        })) || [];
      setOptions(options);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    // Use external options if provided, otherwise fetch from API
    if (externalOptions) {
      setOptions(externalOptions);
    } else {
      fetchResponsibilities();
    }
  }, [externalOptions]);

  const handleSave = async () => {
    if (!newResponsibility.trim()) return;
    try {
      await sv.postName(newResponsibility);
      await fetchResponsibilities();
      setNewResponsibility('');
      setShowModal(false);
    } catch (error) {
      if (error) {
        showError(trans('user.userAlreadyExists'));
      }
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await sv.countAdmin(id);
      if (res.count) {
        toggleConfirm({
          title: 'データが削除されます',
          message:
            `この担当は` +
            res.count +
            `件のアカウントで使用されています。\n削除すると復元できませんがよろしいでしょうか？`,
          okText: '削除',
          cancelText: 'キャンセル',
          width: '448px',
          widthCancel: '160px',
          onOk: async () => {
            await sv.deleteById(id);
            await fetchResponsibilities();

            if (getValues?.(name)?.value == id) {
              onChange?.(null);
            }
          },
        });
      } else {
        toggleConfirm({
          title: 'データが削除されます',
          message: '削除すると復元できませんがよろしいでしょうか？',
          okText: '削除',
          cancelText: 'キャンセル',
          width: '448px',
          onOk: async () => {
            await sv.deleteById(id);
            await fetchResponsibilities();

            if (getValues?.(name)?.value == id) {
              onChange?.(null);
            }
          },
        });
      }
    } catch (error) {
      showError(error);
    }
  };

  const CustomOption = (props: any) => (
    <components.Option {...props}>
      <Flex alignItems="center">
        {!disableDot && (
          <Dot
            border={!props.isSelected}
            color={!props.isSelected ? '#fff' : undefined}
            width={12}
            borderWidth={1}
          />
        )}
        <Text flex={1} mr="10px" truncate>
          {props.data.label}
        </Text>
        {!disableDelete && (
          <BiTrashAlt
            className="ic-del-color"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(props.value);
            }}
          />
        )}
      </Flex>
    </components.Option>
  );

  const CustomGroupHeading = (props: any) => (
    <components.GroupHeading {...props}>
      <div
        style={{
          fontWeight: '600',
          fontSize: '13px',
          color: '#000',
          textTransform: 'capitalize',
        }}
      >
        {props.children}
      </div>
    </components.GroupHeading>
  );

  const CustomMenuList = (props: any) => {
    return (
      <div>
        {!disableCreate && (
          <div
            style={{ padding: '8px 12px' }}
            onClick={(e) => {
              e.stopPropagation();
              setShowModal(true);
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                borderBottom: '1px solid #e2e8f0',
                transition: 'background 0.2s',
                color: '#718096',
                fontSize: '14px',
              }}
            >
              <FaPlus size={14} style={{ marginRight: 8 }} />
              <span style={{ marginTop: '2px' }}>Create a new</span>
            </div>
          </div>
        )}
        <components.MenuList {...props}></components.MenuList>
      </div>
    );
  };

  return (
    <>
      <GridItem {...gridProps}>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <Field label={label} isRequired={isRequired} invalid={!!errors[name]} errorText={errors[name]?.message}>
              <Select
                isDisabled={disabled}
                {...field}
                placeholder={placeholder || label}
                // menuIsOpen={true}
                // closeMenuOnSelect={false}
                // blurInputOnSelect={false}
                options={options}
                isSearchable={!disableSearch}
                noOptionsMessage={() => msgNoOption}
                className="select"
                components={{
                  Option: CustomOption,
                  GroupHeading: CustomGroupHeading,
                  MenuList: CustomMenuList,
                }}
                styles={{
                  control: (styles) => ({
                    ...styles,
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: 15,
                    minHeight: '2.5rem',
                  }),
                  input: (base) => ({
                    ...base,
                    minWidth: 0,
                    fontSize: '14px',
                    color: '#333333'
                  }),
                  menu: (styles) => ({ ...styles, zIndex: 1000 }),
                  option: (styles) => ({
                    ...styles,
                    backgroundColor: '#fff',
                    color: '#000',
                    width: '96%',
                    margin: '0 auto',
                    cursor: 'pointer',
                    ':active': {
                      ...styles[':active'],
                      backgroundColor: '#fff',
                    },
                    ':hover': {
                      ...styles[':hover'],
                      backgroundColor: '#F0F0F0',
                      borderRadius: '10px',
                    },
                    fontSize: 14,
                  }),
                  singleValue: (styles) => ({ ...styles, fontSize: 14 }),
                  indicatorSeparator: () => ({
                    display: 'none',
                  }),
                  placeholder: (styles) => ({
                    ...styles,
                    color: '#A0AEC0',
                    fontSize: placeholderFontSize,
                  }),
                }}
                onChange={(e) => {
                  onChange?.(e);
                  field.onChange(e);
                }}
              />
            </Field>
          )}
        />
        {!disableCreate && (
          <Dialog.Root placement="top" motionPreset="slide-in-bottom" open={showModal}>
            {/* @ts-ignore */}
            <Portal>
              <Dialog.Backdrop />
              <Dialog.Positioner>
                <Dialog.Content
                  style={{
                    width: '448px',
                    maxHeight: 'none', // remove any max height limit to allow hug content
                    minHeight: '198px', // let height grow as per content
                    borderRadius: '12px'
                  }}
                >
                  <Dialog.Header
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    paddingX={'24px'}
                    paddingY={'16px'}
                  >
                    <Dialog.Title style={{ color: '#2D3748' }}>担当 新規作成</Dialog.Title>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowModal(false)}
                      fontSize="lg"
                      colorScheme="gray"
                    >
                      ✕
                    </Button>
                  </Dialog.Header>
                  <Dialog.Body paddingY={'0px'}>
                    <p style={{ marginLeft: '20px', color: '#2D3748', fontWeight: 700 }}>担当名</p>
                    <Flex justify="center" mt={2}>
                      <Input
                        placeholder="例) マネージャー"
                        width={370}
                        height={40}
                        borderRadius="15px"
                        background="white"
                        value={newResponsibility}
                        borderColor={'#4FD1C5'}
                        onChange={(e) => setNewResponsibility(e.target.value)}
                      />
                    </Flex>
                    {newResponsibility.length > 255 && (
                      <Text fontSize="12px" marginLeft={5} color="red">
                        {msgMaxLength(255, '担当名')}
                      </Text>
                    )}
                  </Dialog.Body>
                  <Dialog.Footer paddingY={'10px'}>
                    <Dialog.ActionTrigger asChild>
                      <Button
                        fontWeight="bold"
                        variant="outline"
                        onClick={() => setShowModal(false)}
                        width={134}
                        borderColor="#EDF2F7"
                        bg="#EDF2F7"
                        _hover={{ bg: '#fff' }}
                      >
                        キャンセル
                      </Button>
                    </Dialog.ActionTrigger>
                    <Button
                      bg="#4FD1C5"
                      fontWeight="bold"
                      width={131}
                      color="white"
                      _hover={{ bg: '#38B2AC' }}
                      onClick={handleSave}
                      disabled={!newResponsibility.trim() || newResponsibility.length > 255}
                    >
                      保存
                    </Button>
                  </Dialog.Footer>
                </Dialog.Content>
              </Dialog.Positioner>
            </Portal>
          </Dialog.Root>
        )}
      </GridItem>
    </>
  );
};

export { BaseSelectResponsibility };
